
import React, { useEffect, useState, useRef } from "react";
import {
  Divider,
  Row,
  Col,
  Input,
  Radio,
  message,
  Select,
  Button,
} from "antd";
import axios from "axios";
import "../StepCheckoutStax/StepCheckout.css";
import CommonButton from "components/ButtonWidgets/CommonButton/CommonButton";
import { isEmpty, isNumber } from "utils/GlobalFunctions";
import NormalButton from "components/ButtonWidgets/NormalButton/NormalButton";
import StepItemWidget from "../StepFee/StepItemWidget";
import StepRegItemSummaryWidget from "../StepFee/StepRegItemSummaryWidget";
import buildInfo from '../../../buildInfo.json';
import PaymentProcessingModal from "components/PaymentProcessingModal/PaymentProcessingModal";
import { createPaymentProcessor } from "../../../payments/processors";
import moment from "moment";

// Import merchant-specific sub-components directly (not lazy)
import StaxCardPayment from "../StepCheckoutStax/CardPayment";
import StaxBankPayment from "../StepCheckoutStax/BankPayment";
import ForteCardPayment from "../StepCheckoutForte/CardPayment";
import ForteBankPayment from "../StepCheckoutForte/BankPayment";

// ErrorBoundary for catching render errors
class DebugErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    // eslint-disable-next-line no-console
    console.error('[StepCheckout ERROR BOUNDARY]', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ background: '#fff0f0', color: '#a00', padding: 24, border: '2px solid #a00', margin: 24 }}>
          <h2>StepCheckout Error Boundary</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{String(this.state.error)}</pre>
          <details style={{ fontSize: 12 }}>
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

const { Option } = Select;

// MODULE LEVEL LOG - This should appear when the module loads
// Module loaded

/**
 * Unified StepCheckout Component
 * Supports multiple payment merchants (Stax, Forte) with shared logic
 * and merchant-specific payment processing via processor abstraction
 */

const StepCheckout = (props) => {
  // Always call hooks and destructure props first, before any return or prop validation
  const {
    appInfo,
    userData,
    setUserData,
    errorField,
    setErrorField,
    onRegister,
    recordList,
    onPaymentFail,
    regType,
    merchant = "stax", // Default to stax
  } = props;

  const paymentProcessor = useRef(null);
  const [isPayButtonDisabled, setIsPayButtonDisabled] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ 
    visible: false, 
    status: 'processing', 
    message: '', 
    error: '' 
  });
  const [dcAmount, setDcAmount] = useState(0);
  const [disCount, setDisCount] = useState("");
  const [codeValid, setCodeValid] = useState(true);
  const [cardType, setCardType] = useState("credit");
  const [processorLoading, setProcessorLoading] = useState(true);
  // Restore allowManualPayments and emailInvoicesToAdmin from registration info if present
  const [allowManualPayments, setAllowManualPayments] = useState(false);
  const [emailInvoicesToAdmin, setEmailInvoicesToAdmin] = useState(false);
  // No checkbox: always send invoice to admin if enabled
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpire] = useState("");
  const [cvc, setCvc] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountType, setAccountType] = useState("c");
  const merchantLower = merchant ? merchant.toLowerCase() : '';
  const hasInitRef = useRef(false);
  const isMountedRef = useRef(true);
  const lastMerchantRef = useRef(merchantLower);

  // Check if this is a facility registration type
  const isFacilityType = appInfo?.registration_info?.registration_type === 'facility';

  // Check if total amount is $0 (either originally or after discount)
  // Hide billing/payment fields and show "Register" instead of "Pay"
  const isZeroAmount = !isFacilityType && (
    (parseFloat(userData?.total_amount || 0) === 0) ||
    (userData?.total_checkout !== undefined && parseFloat(userData.total_checkout) === 0)
  );

  // Debug logging removed for clarity

  // Initialize payment processor
  useEffect(() => {
    // Reset init flag when merchant changes and mark mounted
    if (lastMerchantRef.current !== merchantLower) {
      lastMerchantRef.current = merchantLower;
      hasInitRef.current = false;
    }
    isMountedRef.current = true;

    // Skip initialization if required props are missing or amount is $0
    if (!appInfo || !merchant) {
      return;
    }
    if (parseFloat(userData?.total_amount || 0) === 0) {
      setProcessorLoading(false);
      setIsPayButtonDisabled(false);
      return;
    }
    // Log resolved merchant values to verify unified checkout selection
    console.log('[StepCheckout] merchant resolution', {
      merchantProp: merchant,
      merchantLower,
      regMerchant: appInfo?.registration_info?.merchant
    });

    const initializeProcessor = async () => {
      try {
        setProcessorLoading(true);

        // Validate merchant configuration
        if (!merchant) {
          throw new Error('Invalid Merchant Configuration: Merchant type not specified');
        }

        // Create processor instance
        paymentProcessor.current = createPaymentProcessor(merchant);

        // Initialize with merchant-specific options
        const paymentDomain = appInfo.registration_info?.domain || window.location.hostname;
        if (merchantLower === "stax") {
          await paymentProcessor.current.initialize({
            domain: paymentDomain,
            onCardFormComplete: () => {
              setIsPayButtonDisabled(false);
            },
            onCardFormUncomplete: () => {
              setIsPayButtonDisabled(true);
            },
          });
        } else if (merchantLower === "forte") {
          await paymentProcessor.current.initialize({
            domain: paymentDomain,
            merchant: merchant,
          });
          // Forte doesn't use form validation, always enable button
          setIsPayButtonDisabled(false);
        }

        // Set processorLoading to false to reveal the form
        // ALWAYS set processorLoading to false after successful init
        setProcessorLoading(false);

        // For Stax, call showCardForm after a delay to ensure DOM elements are rendered
        if (merchantLower === "stax" && paymentProcessor.current) {
          setTimeout(() => {
            paymentProcessor.current.showCardForm();
          }, 500);
        }
      } catch (error) {
        // Even on error, set processorLoading to false so UI is not stuck
        setProcessorLoading(false);

        // Ignore undefined errors (these are often from React DevTools or unmounting during async operations)
        if (error === undefined) {
          return;
        }

        // Robust error handling: handle non-Error values
        console.error('Initialize processor error', {
          error,
          errorType: typeof error,
          errorConstructor: error?.constructor?.name,
          merchant: merchantLower,
          domain: appInfo?.registration_info?.domain,
          stack: error?.stack
        });
        const errMsg =
          (error && error.message)
            ? error.message
            : (typeof error === 'string' && error.length > 0)
              ? error
              : 'Failed to load payment configuration. Please refresh and try again.';
        try {
          message.error(errMsg);
        } catch (_) {
          // no-op if message API not available
        }
        if (isMountedRef.current && typeof setProcessorLoading === 'function') {
          try {
            setProcessorLoading(false);
          } catch (stateError) {
            console.error('setState error in catch:', stateError);
          }
        }
      }
    };

    // Only initialize if we have registration info loaded and not already initialized
    if (appInfo?.registration_info) {
      if (!hasInitRef.current) {
        hasInitRef.current = true;
        initializeProcessor();
      } else {
        // already started, skip duplicate init
      }
    } else {
      // registration_info not yet present, skipping init
    }

    return () => {
      // Cleanup processor on unmount
      isMountedRef.current = false;
      if (paymentProcessor.current) {
        paymentProcessor.current.destroy();
      }
    };
    // NOTE: Only depend on appInfo and merchant - NOT userData
    // userData changes frequently (on every input) and would destroy/reinit the processor
  }, [appInfo, merchant, merchantLower]);

  // Set billing info from available data if not already filled (separate useEffect to avoid re-render loop)
  useEffect(() => {
    if (!userData || !setUserData) return;
    
    const regTypeValue = (appInfo?.registration_info?.registration_type || "")
      .toString()
      .trim()
      .toLowerCase();
    const isFacilityType = regTypeValue === "facility";

    const contactFirst = userData.contact_first_name || userData.camperInfo?.[0]?.contact_first_name || "";
    const contactLast = userData.contact_last_name || userData.camperInfo?.[0]?.contact_last_name || "";
    const contactAddress = userData.contact_address || userData.camperInfo?.[0]?.contact_address || "";
    const contactCity = userData.contact_city || userData.camperInfo?.[0]?.contact_city || "";
    const contactState = userData.contact_state || userData.camperInfo?.[0]?.contact_state || "";
    const contactZip = userData.contact_zip || userData.camperInfo?.[0]?.contact_zip || "";
    const contactPhone = userData.contact_phone || userData.camperInfo?.[0]?.contact_phone || "";

    const billMatchesParent =
      userData.bill_first_name === userData.p_first_name &&
      userData.bill_last_name === userData.p_last_name;

    // Only autofill if billing fields are empty, or if facility and billing still matches parent
    if (!isFacilityType && userData.bill_first_name) return;
    if (isFacilityType && userData.bill_first_name && !billMatchesParent) return;
    
    console.log('[StepCheckout] Autofilling billing info from available data');
    const firstCamper = userData.camperInfo && userData.camperInfo.length > 0 ? userData.camperInfo[0] : null;
    console.log('[StepCheckout] billing autofill context', regTypeValue, {
      contact_first_name: contactFirst,
      contact_last_name: contactLast,
      contact_address: contactAddress,
      contact_city: contactCity,
      contact_state: contactState,
      contact_zip: contactZip,
      p_first_name: userData.p_first_name,
      p_last_name: userData.p_last_name,
      camper_first_name: firstCamper?.camper_first_name,
      camper_last_name: firstCamper?.camper_last_name
    });
    
    let billingData;
    
    if (isFacilityType) {
      // For facility registrations, use contact_* fields
      billingData = {
        bill_first_name: userData.bill_first_name || contactFirst || "",
        bill_last_name: userData.bill_last_name || contactLast || "",
        bill_address: userData.bill_address || contactAddress || "",
        bill_city: userData.bill_city || contactCity || "",
        bill_state: userData.bill_state || contactState || "",
        bill_postal_code: userData.bill_postal_code || contactZip || ""
      };
    } else {
      // For camp registrations, use fallback hierarchy:
      // 1. Try billing_xxxx fields (if entered in step 1)
      // 2. Try parent p_xxxx fields
      // 3. Try first camper's camper_xxxx fields
      billingData = {
        bill_first_name: userData.bill_first_name || userData.p_first_name || firstCamper?.camper_first_name || "",
        bill_last_name: userData.bill_last_name || userData.p_last_name || firstCamper?.camper_last_name || "",
        bill_address: userData.bill_address || userData.p_address || firstCamper?.camper_street || "",
        bill_city: userData.bill_city || userData.p_city || firstCamper?.camper_city || "",
        bill_state: userData.bill_state || userData.p_state || firstCamper?.camper_state || "",
        bill_postal_code: userData.bill_postal_code || userData.p_postal_code || firstCamper?.camper_zip || ""
      };
    }
    
    // Only update if we have at least first and last name
    console.log('[StepCheckout] billing autofill result', billingData);
    if (billingData.bill_first_name && billingData.bill_last_name) {
      setUserData({
        ...userData,
        ...billingData,
        ...(isFacilityType && !userData.p_cell_phone && contactPhone
          ? { p_cell_phone: contactPhone }
          : {})
      });
    }
  }, [userData?.p_first_name, userData?.p_last_name, userData?.contact_first_name, userData?.contact_last_name, userData?.contact_address, userData?.contact_city, userData?.contact_state, userData?.contact_zip, userData?.bill_first_name, userData?.bill_address, userData?.camperInfo, appInfo?.registration_info?.registration_type, setUserData]);

  // Fetch registration payment settings and restore manual payments/email to admin
  useEffect(() => {
    if (!appInfo?.registration_info?.ID) {
      return;
    }

    const fetchPaymentSettings = async () => {
      try {
        const regInfo = appInfo.registration_info;
        // For linked registrations, payment settings live on the parent registration
        const registration_id = regInfo?.registration_type === 'linked'
          ? (regInfo?.parent_registration_id || regInfo?.ID)
          : regInfo?.ID;
        const response = await axios.post(
          `${process.env.REACT_APP_API || 'https://dev.beclutch.club/api'}/camp/get_registration_payment_settings`,
          { registration_id }
        );
        if (response.data.success || response.data.result) {
          // API returns data in response.data.data wrapper
          const settingsData = response.data.data || response.data;

          if (typeof settingsData.allow_manual_payments !== 'undefined') {
            setAllowManualPayments(!!settingsData.allow_manual_payments);
          }
          if (typeof settingsData.email_invoices_to_admin !== 'undefined') {
            setEmailInvoicesToAdmin(!!settingsData.email_invoices_to_admin);
          }
        }
      } catch (error) {
        console.error('Failed to fetch payment settings:', error);
      }
    };
    fetchPaymentSettings();
  }, [appInfo?.registration_info?.ID]);

  // Initialize fee calculation on mount to ensure processing_fee is set with defaults
  // This fixes the issue where fees weren't being calculated if feeInfo was null in RegisterPage
  useEffect(() => {
    if (userData?.total_amount && !processorLoading) {
      // Calculate initial fee based on default card type (credit)
      setTotalAmount(cardType, dcAmount, disCount);
    }
  }, [processorLoading]); // Run when processor finishes loading

  // Show card/bank form when card type changes (Stax only)
  useEffect(() => {
    if (merchantLower === "stax" && paymentProcessor.current) {
      setTimeout(() => {
        if (cardType === "echeck") {
          paymentProcessor.current.showBankForm();
        } else {
          paymentProcessor.current.showCardForm();
        }
      }, 250);
    }
  }, [cardType, merchantLower]);

  // Ensure Stax card form is shown on initial mount if needed
  useEffect(() => {
    // DEBUG: Log when this effect runs
    window.console && window.console.log && window.console.log('[StaxCardInject useEffect] TRIGGERED with deps:', {
      merchantLower,
      processorLoading,
      cardType,
    });

    let retryCount = 0;
    const maxRetries = 10;
    const retryDelay = 300;
    function tryInjectCardForm() {
      window.console && window.console.log && window.console.log('[StaxCardInject] Attempt', retryCount + 1, {
        merchantLower,
        processorReady: !!paymentProcessor.current,
        processorLoading,
        cardType,
      });
      if (
        merchantLower === "stax" &&
        paymentProcessor.current &&
        !processorLoading &&
        (cardType === "credit" || cardType === "debit")
      ) {
        const cardNumberDiv = document.getElementById("card-number");
        const cardCvvDiv = document.getElementById("card-cvv");
        const cardNumberInjected = cardNumberDiv && cardNumberDiv.children.length > 0;
        const cardCvvInjected = cardCvvDiv && cardCvvDiv.children.length > 0;
        window.console && window.console.log && window.console.log('[StaxCardInject] card-number div:', cardNumberDiv, 'children:', cardNumberDiv?.children.length);
        window.console && window.console.log && window.console.log('[StaxCardInject] card-cvv div:', cardCvvDiv, 'children:', cardCvvDiv?.children.length);
        if (!window.StaxJs) {
          window.console && window.console.warn && window.console.warn('[StaxCardInject] window.StaxJs is NOT loaded!');
        } else {
          window.console && window.console.log && window.console.log('[StaxCardInject] window.StaxJs is loaded.');
        }
        if (!cardNumberInjected || !cardCvvInjected) {
          window.console && window.console.log && window.console.log('[StaxCardInject] Calling showCardForm()');
          paymentProcessor.current.showCardForm();
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(tryInjectCardForm, retryDelay);
          } else {
            window.console && window.console.error && window.console.error('[StaxCardInject] Max retries reached. Card fields not injected.');
          }
        } else {
          window.console && window.console.log && window.console.log('[StaxCardInject] Card fields successfully injected.');
        }
      } else {
        window.console && window.console.log && window.console.log('[StaxCardInject] Conditions not met for injection.');
      }
    }
    tryInjectCardForm();
    // No cleanup needed
  }, [merchantLower, processorLoading, cardType]);

  // Render debug UI for missing props if needed (AFTER all hooks)
  if (!appInfo || !userData || !merchant) {
    return (
      <div style={{ background: '#fffbe6', color: '#ad8b00', padding: 24, border: '2px solid #ad8b00', margin: 24 }}>
        <h2>StepCheckout: Missing required prop(s)</h2>
        {!appInfo && <div>appInfo is missing</div>}
        {!userData && <div>userData is missing</div>}
        {!merchant && <div>merchant is missing</div>}
        <pre>{JSON.stringify(props, null, 2)}</pre>
      </div>
    );
  }

  const validateFields = () => {
    let errorList = [];

    // Skip billing and payment validation for $0 registrations
    if (isZeroAmount) {
      setErrorField([]);
      return true;
    }

    errorList = isEmpty(userData, "bill_first_name", errorList);
    errorList = isEmpty(userData, "bill_last_name", errorList);
    errorList = isEmpty(userData, "bill_address", errorList);
    errorList = isEmpty(userData, "bill_city", errorList);
    errorList = isEmpty(userData, "bill_state", errorList);
    errorList = isEmpty(userData, "bill_postal_code", errorList);
    errorList = isEmpty(userData, "p_cell_phone", errorList);

    if (cardType !== "echeck") {
      // Card payment validation
      if (merchantLower === "forte") {
        // Forte: validate local card state with individual field errors
        if (!cardNumber || cardNumber.replace(/\s/g, '').length < 15) {
          errorList.push("cardNumber");
        }
        // Validate expiry: DatePicker provides MM/YY format
        if (!expiry || expiry.length < 5) {
          errorList.push("expiry");
        }
        if (!cvc || cvc.length < 3) {
          errorList.push("cvc");
        }
      } else {
        // Stax: validate expiration in userData
        errorList = isEmpty(userData, "expiration", errorList);
      }
    } else {
      // eCheck validation
      if (merchantLower === "forte") {
        // Forte: validate local eCheck state with individual field errors
        if (!routingNumber || routingNumber.length < 9) {
          errorList.push("routingNumber");
        }
        if (!accountNumber || accountNumber.length < 4) {
          errorList.push("accountNumber");
        }
      } else {
        // Stax: validate eCheck fields in userData
        errorList = isEmpty(userData, "bank_name", errorList);
        errorList = isEmpty(userData, "bank_type", errorList);
        errorList = isEmpty(userData, "bank_holder_type", errorList);
        errorList = isEmpty(userData, "bank_account", errorList);
        errorList = isEmpty(userData, "bank_routing", errorList);
      }
    }

    setErrorField([...errorList]);
    return errorList.length === 0;
  };

  const onApply = () => {
    let codeList = [];
    if (userData?.disCountInfo) {
      codeList = [...userData?.disCountInfo];
    }
    if (disCount !== "") {
      let isValid = false;
      for (let i = 0; i < codeList.length; i++) {
        if (disCount === codeList[i].discount_code) {
          isValid = true;
          break;
        }
      }
      setCodeValid(isValid);
      if (isValid) {
        let disCountAmount = 0;
        let selDisInfo = codeList.filter((x) => x.discount_code === disCount)[0];
        if (parseInt(selDisInfo.discount_free) === 1) {
          disCountAmount = parseFloat(userData.total_amount);
        } else {
          if (parseFloat(selDisInfo.discount_percentage) > 0) {
            disCountAmount =
              (parseFloat(selDisInfo.discount_percentage) / 100) *
              userData.total_amount;
          } else if (parseFloat(selDisInfo.discount_amount) > 0) {
            disCountAmount = parseFloat(selDisInfo.discount_amount);
          } else {
            disCountAmount = 0;
          }
        }
        setDcAmount(disCountAmount);
        setTotalAmount(cardType, disCountAmount, disCount);
      }
    }
  };

  const onChangeCardType = (e) => {
    setCardType(e.target.value);
    setTotalAmount(e.target.value, dcAmount, disCount);
  };

  const setTotalAmount = (paymentType, amount, code) => {
    let processing_fee = 0;
    let countedTotal = parseFloat(userData.total_amount - parseFloat(amount)).toFixed(2);

    // Get fee configuration from appInfo (club_registrations table)
    const regInfo = appInfo.registration_info || appInfo.registration_info;
    const creditFee = parseFloat(regInfo?.credit_fee || 3.65) / 100; // Convert percentage to decimal
    const creditMin = parseFloat(regInfo?.credit_min || 0);
    const debitFee = parseFloat(regInfo?.debit_fee || 3.65) / 100;
    const debitMin = parseFloat(regInfo?.debit_min || 0);
    const debitFlatOrPercentage = regInfo?.debit_flat_or_percentage;
    const echeckFee = parseFloat(regInfo?.echeck_fee || 2);

    // Calculate processing fee based on payment type
    if (paymentType === "credit") {
      processing_fee = parseFloat(countedTotal * creditFee).toFixed(2);
      if (parseFloat(processing_fee) < creditMin) {
        processing_fee = creditMin.toFixed(2);
      }
    } else if (paymentType === "debit") {
      if (debitFlatOrPercentage === "1" || debitFlatOrPercentage === 1) {
        // Flat fee
        processing_fee = parseFloat(regInfo?.debit_fee).toFixed(2);
      } else {
        // Percentage-based
        processing_fee = parseFloat(countedTotal * debitFee).toFixed(2);
        if (debitMin && parseFloat(processing_fee) < parseFloat(debitMin)) {
          processing_fee = parseFloat(debitMin).toFixed(2);
        }
      }
    } else if (paymentType === "echeck") {
      // eCheck is typically a flat fee
      processing_fee = echeckFee.toFixed(2);
    }

    // If registration total is $0 (originally or after discount), no processing fee applies
    if (parseFloat(countedTotal) === 0) {
      processing_fee = 0;
    }

    // If discount code is applied, no processing fee (per Forte logic)
    if (code !== "") {
      processing_fee = 0;
    }

    countedTotal = parseFloat(parseFloat(countedTotal) + parseFloat(processing_fee)).toFixed(2);

    setUserData({
      ...userData,
      discount_amount: amount,
      discount_code: code,
      total_checkout: countedTotal,
      processing_fee: processing_fee,
    });
  };

  const onPay = async () => {
    if (isBusy) return;

    // Free registration
    if (parseFloat(userData.total_checkout) === 0) {
      setUserData({
        ...userData,
        isPayment: true,
        payment_method_id: "",
        customer_id: "",
        payment_method: cardType,
      });
      setTimeout(() => {
        onRegister(cardType);
      }, 500);
      return;
    }

    // Validate fields
    if (!validateFields()) {
      message.error("Please confirm payment method information");
      return;
    }

    // Stax-specific: check if pay button is enabled
    if (merchantLower === "stax" && cardType !== "echeck" && isPayButtonDisabled) {
      message.error("Please input valid payment information");
      return;
    }

    // Check if already paid
    if (userData.isPayment) {
      message.success("Payment has been already done successfully");
      setUserData({
        ...userData,
        isPayment: true,
        payment_method_id: "",
        customer_id: "",
        payment_method: cardType,
      });
      setTimeout(() => {
        onRegister(cardType);
      }, 500);
      return;
    }

    // Process payment
    onPaymentCall();
  };

  const onPaymentCall = async () => {
    if (isBusy) return;
    setIsBusy(true);

    setPaymentModal({ 
      visible: true, 
      status: 'processing', 
      message: 'Processing Payment...', 
      error: '' 
    });

    try {
      const regInfo = appInfo?.registration_info || {};

      // Build payment data based on merchant
      const paymentData = {
        cardType,
        userData,
        registrationInfo: regInfo,
        totalCheckout: userData.total_checkout,
        processingFee: userData.processing_fee,
        creditMin: regInfo?.credit_min || 0,
      };

      // Add Forte-specific fields
      if (merchantLower === "forte") {
        paymentData.cardNumber = cardNumber;
        paymentData.expiry = expiry;
        paymentData.cvc = cvc;
        paymentData.routingNumber = routingNumber;
        paymentData.accountNumber = accountNumber;
        paymentData.accountType = accountType;
      }

      // Process payment through processor
      const result = await paymentProcessor.current.processPayment(paymentData);

      if (result.success) {
        // Show success modal
        setPaymentModal({ 
          visible: true, 
          status: 'success', 
          message: 'Payment Successful!', 
          error: '' 
        });

        setUserData({
          ...userData,
          isPayment: true,
          payment_method_id: result.paymentMethodId || result.transactionId,
          customer_id: result.customerId || "",
          payment_method: cardType,
        });

        setTimeout(() => {
          setPaymentModal({ visible: false, status: 'processing', message: '', error: '' });
          setIsBusy(false);
          
          // Call onRegister with merchant-specific parameters
          if (merchantLower === "forte") {
            onRegister(
              cardType,
              result.transactionId,
              result.customerId,
              result.authorizationCode,
              result.locationId
            );
          } else {
            onRegister(cardType, result.transactionId, result.customerId);
          }
        }, 1500);
      } else {
        throw new Error("Payment processing failed");
      }
    } catch (err) {
      setIsBusy(false);

      const errorDetail = err.response?.data?.msg ||
        err.response?.data?.message ||
        err.message ||
        "Payment failed";

      console.error("Payment error:", err);

      setPaymentModal({
        visible: true,
        status: 'error',
        message: 'Payment Failed',
        error: errorDetail
      });

      onPaymentFail(cardType, err.message || String(err), "transaction_api", {
        card_last4: merchantLower === "forte" && cardNumber ? cardNumber.replace(/\s/g, '').slice(-4) : "",
        expiry: merchantLower === "forte" ? (expiry || "") : "",
        merchant_error_detail: JSON.stringify(err.response?.data || err.message || String(err)),
      });
    }
  };

  const onDropOffPayment = async () => {
    if (isBusy) return;
    setIsBusy(true);

    setPaymentModal({ 
      visible: true, 
      status: 'processing', 
      message: 'Processing Drop-Off Registration...', 
      error: '' 
    });

    try {
      const registration_id = appInfo.registration_info.ID;
      if (!registration_id) {
        throw new Error('Registration ID not found');
      }

      const manualTotalCheckout = parseFloat(
        (parseFloat(userData.total_amount || 0) - parseFloat(userData.discount_amount || 0)).toFixed(2)
      );

      const payload = {
        registration_id,
        manual_payment_code: disCount,
        ...userData,
        processing_fee: 0,
        total_checkout: manualTotalCheckout,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API || 'https://dev.beclutch.club/api'}/camp/manual_payment_registration`,
        payload
      );

      if (response.data && response.data.success) {
        setPaymentModal({
          visible: true,
          status: 'success',
          message: 'Invoice Sent Successfully!',
          error: ''
        });

        setTimeout(() => {
          setIsBusy(false);
          setPaymentModal({ visible: false, status: 'processing', message: '', error: '' });
          onRegister("manual", response.data.transaction_id || "", response.data.customer_id || "");
        }, 1500);
      } else {
        throw new Error(response.data?.message || 'Failed to process drop-off payment');
      }
    } catch (err) {
      setIsBusy(false);

      const errorDetail = err.response?.data?.msg ||
        err.response?.data?.message ||
        err.message ||
        "Drop-off registration failed";

      console.error("Drop-off payment error:", err);

      setPaymentModal({
        visible: true,
        status: 'error',
        message: 'Drop-Off Registration Failed',
        error: errorDetail
      });
    }
  };

  // Submit facility application (for facility registration types - no payment)
  const onSubmitFacilityApplication = async () => {
    if (isBusy) return;
    setIsBusy(true);

    setPaymentModal({
      visible: true,
      status: 'processing',
      message: 'Submitting Application...',
      error: ''
    });

    try {
      const registration_id = appInfo.registration_info.ID;
      if (!registration_id) {
        throw new Error('Registration ID not found');
      }

      // Collect facility data from userData (populated from form fields)
      // Convert alcohol Yes/No to value that backend can convert to tinyint
      const normalizeDateValue = (value) => {
        if (!value) return "";
        const parsed = moment(value, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
        return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
      };

      const normalizeTimeValue = (value) => {
        if (!value) return "";
        const parsed = moment(value, ["HH:mm:ss", "HH:mm", "hh:mm A", "h:mm A"], true);
        return parsed.isValid() ? parsed.format("HH:mm:ss") : value;
      };

      const rawEventDate = userData.event_date || userData?.camperInfo?.[0]?.event_date || "";
      const rawStartTime = userData.event_start_time || userData?.camperInfo?.[0]?.event_start_time || "";
      const rawEndTime = userData.event_end_time || userData?.camperInfo?.[0]?.event_end_time || "";

      const firstParticipant = userData?.camperInfo?.[0] || {};
      const facilityData = {
        registration_id,
        // Map userData fields to facility fields
        facility: userData.facility || userData.sel_facility || '',
        applicant_name: userData.applicant_name || `${userData.p_first_name || ''} ${userData.p_last_name || ''}`.trim(),
        applicant_phone: userData.applicant_phone || userData.p_cell_phone || '',
        contact_first_name: userData.contact_first_name || userData.p_first_name || '',
        contact_last_name: userData.contact_last_name || userData.p_last_name || '',
        contact_phone: userData.contact_phone || userData.p_cell_phone || '',
        contact_email: userData.contact_email || userData.p_email || firstParticipant.contact_email || firstParticipant.p_email || firstParticipant.email || '',
        contact_address: userData.contact_address || userData.p_address || '',
        contact_city: userData.contact_city || userData.p_city || '',
        contact_state: userData.contact_state || userData.p_state || '',
        contact_zip: userData.contact_zip || userData.p_zip || '',
        event_type: userData.event_type || '',
        number_attendees: userData.number_attendees || '',
        event_date: normalizeDateValue(rawEventDate),
        event_start_time: normalizeTimeValue(rawStartTime),
        event_end_time: normalizeTimeValue(rawEndTime),
        total_hours: userData.camperInfo?.[0]?.total_hours || userData.total_hours || 0,
        notes: userData.notes || userData.additional_notes || '',
        alcohol: userData.alcohol || '', // Backend converts Yes/No to 1/0
        insurance_company: userData.insurance_company || '',
        gl_policy_number: userData.gl_policy_number || '',
        gl_umbrella_policy_number: userData.gl_umbrella_policy_number || '',
        step1_participants: userData.camperInfo || [],
        waiver_info: JSON.stringify(userData.waiverList || []),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API || 'https://dev.beclutch.club/api'}/camp/submit_facility_application`,
        facilityData
      );

      if (response.data && (response.data.result || response.data.success)) {
        setPaymentModal({
          visible: true,
          status: 'success',
          message: 'Application Submitted Successfully!',
          error: ''
        });

        setTimeout(() => {
          setIsBusy(false);
          setPaymentModal({ visible: false, status: 'processing', message: '', error: '' });
          // Call onRegister with 'facility' type to indicate successful submission
          onRegister("facility", response.data.facility_registration_id || "", "");
        }, 1500);
      } else {
        throw new Error(response.data?.msg || response.data?.message || 'Failed to submit application');
      }
    } catch (err) {
      setIsBusy(false);

      const errorDetail = err.response?.data?.msg ||
        err.response?.data?.message ||
        err.message ||
        "Application submission failed";

      console.error("Facility application error:", err);

      setPaymentModal({
        visible: true,
        status: 'error',
        message: 'Application Submission Failed',
        error: errorDetail
      });
    }
  };

  // Get merchant-specific components
  const CardPayment = merchantLower === "stax" ? StaxCardPayment : ForteCardPayment;
  const BankPayment = merchantLower === "stax" ? StaxBankPayment : ForteBankPayment;

  // Safety: ensure userData has required arrays before rendering
  const camperInfo = userData?.camperInfo || [];
  const tripList = userData?.tripList || [];
  const disCountInfo = userData?.disCountInfo || [];

  // Get registration info with safety
  const regInfo = appInfo?.registration_info || {};
  const creditFee = regInfo.credit_fee ?? 0;
  const debitFee = regInfo.debit_fee ?? 0;
  const echeckFee = parseFloat(regInfo.echeck_fee || 0);

  // Pre-render check removed for cleaner console

  // Early return if critical data is missing
  if (!appInfo?.registration_info) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div>Loading checkout...</div>
      </div>
    );
  }



  // FULL CHECKOUT UI RESTORED WITH DEBUG WRAPPERS

  return (
    <DebugErrorBoundary>
      <div className="step-checkout-container" style={{ backgroundColor: '#f0f0f0', minHeight: '400px', padding: '20px', maxWidth: 600, margin: '0 auto' }}>
        <h2>Step 3: Checkout</h2>
        <Divider />
        {/* Registration Summary */}
        <div style={{ marginBottom: 16 }}>
          {camperInfo.map((camper, camperIndex) => (
            <div key={camperIndex} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 16, color: "var(--blueColor)", marginBottom: 8, textAlign: 'left', paddingLeft: 0 }}>
                {camper?.camper_first_name && camper?.camper_last_name
                  ? `${camper.camper_first_name} ${camper.camper_last_name}`
                  : `Participant ${camperIndex + 1}`}
              </div>
              {/* Selected Registrations */}
              {camper.sel_reg_id && camper.sel_reg_id.length > 0 && camper.sel_reg_id.map((regItem, regIndex) => (
                <StepRegItemSummaryWidget
                  key={regIndex}
                  itemInfo={regItem}
                  camperIndex={camperIndex}
                  userData={userData}
                  style={{ textAlign: 'left', paddingLeft: 0 }}
                />
              ))}
              {/* Selected Extra Programs/Trips */}
              {tripList.length > 0 && tripList.filter(trip =>
                camper.sel_trip && camper.sel_trip.includes(trip.id)
              ).map((tripItem, tripIndex) => (
                <StepItemWidget
                  key={tripIndex}
                  itemInfo={tripItem}
                  camperIndex={camperIndex}
                  userData={userData}
                  hideCheckbox={true}
                  style={{ textAlign: 'left', paddingLeft: 0 }}
                />
              ))}
            </div>
          ))}
        </div>
        <Divider />
        {/* Discount Code Section */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, textAlign: 'left' }}>Discount Code</div>
          <Row gutter={8} align="middle">
            <Col flex="auto">
              <Input
                value={disCount}
                onChange={(e) => setDisCount(e.target.value)}
                placeholder="Enter discount code"
                className={!codeValid ? "common-input-error" : "common-input"}
                style={{ textAlign: 'left' }}
              />
            </Col>
            <Col>
              <CommonButton onClick={onApply}>Apply</CommonButton>
            </Col>
          </Row>
          {!codeValid && (
            <div style={{ color: 'red', fontSize: 12, marginTop: 4, textAlign: 'left' }}>Invalid discount code</div>
          )}
          {dcAmount > 0 && (
            <div style={{ color: 'green', fontSize: 14, marginTop: 8, textAlign: 'left' }}>
              Discount Applied: -${parseFloat(dcAmount).toFixed(2)}
            </div>
          )}
        </div>
        {/* Fee Summary */}
        <div style={{ marginTop: 16, padding: 16, background: '#fafafa', borderRadius: 4 }}>
          <Row justify="space-between" style={{ marginBottom: 8 }} align="middle">
            <Col style={{ textAlign: 'left', minWidth: 120 }}>Subtotal:</Col>
            <Col style={{ textAlign: 'right', minWidth: 80 }}>${parseFloat(userData?.total_amount || 0).toFixed(2)}</Col>
          </Row>
          {dcAmount > 0 && (
            <Row justify="space-between" style={{ marginBottom: 8, color: 'green' }} align="middle">
              <Col style={{ textAlign: 'left', minWidth: 120 }}>Discount:</Col>
              <Col style={{ textAlign: 'right', minWidth: 80 }}>-${parseFloat(dcAmount).toFixed(2)}</Col>
            </Row>
          )}
          <Row justify="space-between" style={{ marginBottom: 8 }} align="middle">
            <Col style={{ textAlign: 'left', minWidth: 120 }}>
              {cardType === 'debit' ? 'Administrative Fee' : 'Processing Fee'} ({cardType}):
            </Col>
            <Col style={{ textAlign: 'right', minWidth: 80 }}>${parseFloat(userData?.processing_fee || 0).toFixed(2)}</Col>
          </Row>
          <Divider style={{ margin: '8px 0' }} />
          <Row justify="space-between" style={{ fontWeight: 'bold', fontSize: 16 }} align="middle">
            <Col style={{ textAlign: 'left', minWidth: 120 }}>Total:</Col>
            <Col style={{ textAlign: 'right', minWidth: 80 }}>${parseFloat(userData?.total_checkout || 0).toFixed(2)}</Col>
          </Row>
        </div>
        <Divider />
        {/* Billing Information Section - Hidden when $0 */}
        {!isZeroAmount && <div style={{ marginBottom: 24 }}>
          <h3 style={{ textAlign: 'left' }}>Billing Information</h3>
          <Row gutter={16} style={{ marginTop: 10 }}>
            <Col xs={24} sm={12}>
              <div style={{ paddingBottom: "5px", textAlign: 'left' }}>Billing First Name*</div>
              <Input
                value={userData?.bill_first_name || ""}
                className={
                  errorField?.includes("bill_first_name")
                    ? `common-input-error`
                    : `common-input`
                }
                onChange={(e) => {
                  if (errorField?.includes("bill_first_name")) {
                    let errors = errorField.filter((x) => x !== "bill_first_name");
                    setErrorField([...errors]);
                  }
                  setUserData({ ...userData, bill_first_name: e.target.value });
                }}
                style={{ textAlign: 'left' }}
              />
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ paddingBottom: "5px", textAlign: 'left' }}>Billing Last Name*</div>
              <Input
                value={userData?.bill_last_name || ""}
                className={
                  errorField?.includes("bill_last_name")
                    ? `common-input-error`
                    : `common-input`
                }
                onChange={(e) => {
                  if (errorField?.includes("bill_last_name")) {
                    let errors = errorField.filter((x) => x !== "bill_last_name");
                    setErrorField([...errors]);
                  }
                  setUserData({ ...userData, bill_last_name: e.target.value });
                }}
                style={{ textAlign: 'left' }}
              />
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 10 }}>
            <Col xs={24} sm={12}>
              <div style={{ paddingBottom: "5px", textAlign: 'left' }}>Billing Address*</div>
              <Input
                value={userData?.bill_address || ""}
                className={
                  errorField?.includes("bill_address")
                    ? `common-input-error`
                    : `common-input`
                }
                onChange={(e) => {
                  if (errorField?.includes("bill_address")) {
                    let errors = errorField.filter((x) => x !== "bill_address");
                    setErrorField([...errors]);
                  }
                  setUserData({ ...userData, bill_address: e.target.value });
                }}
                style={{ textAlign: 'left' }}
              />
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ paddingBottom: "5px", textAlign: 'left' }}>Billing City*</div>
              <Input
                value={userData?.bill_city || ""}
                className={
                  errorField?.includes("bill_city")
                    ? `common-input-error`
                    : `common-input`
                }
                onChange={(e) => {
                  if (errorField?.includes("bill_city")) {
                    let errors = errorField.filter((x) => x !== "bill_city");
                    setErrorField([...errors]);
                  }
                  setUserData({ ...userData, bill_city: e.target.value });
                }}
                style={{ textAlign: 'left' }}
              />
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 10 }}>
            <Col xs={24} sm={8}>
              <div style={{ paddingBottom: "5px", textAlign: 'left' }}>State/Province*</div>
              <Select
                showSearch
                style={{ width: '100%', textAlign: 'left' }}
                className={
                  errorField?.includes("bill_state")
                    ? `common-select-error`
                    : `common-select`
                }
                value={userData?.bill_state || undefined}
                placeholder="Select state"
                onSelect={(v) => {
                  if (errorField?.includes("bill_state")) {
                    let errors = errorField.filter((x) => x !== "bill_state");
                    setErrorField([...errors]);
                  }
                  setUserData({ ...userData, bill_state: v });
                }}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {/* ...existing code... */}
              </Select>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ paddingBottom: "5px", textAlign: 'left' }}>Zip/Postal Code*</div>
              <Input
                value={userData?.bill_postal_code || ""}
                className={
                  errorField?.includes("bill_postal_code")
                    ? `common-input-error`
                    : `common-input`
                }
                onChange={(e) => {
                  if (errorField?.includes("bill_postal_code")) {
                    let errors = errorField.filter((x) => x !== "bill_postal_code");
                    setErrorField([...errors]);
                  }
                  setUserData({ ...userData, bill_postal_code: e.target.value });
                }}
                style={{ textAlign: 'left' }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ paddingBottom: "5px", textAlign: 'left' }}>Phone Number*</div>
              <Input
                type="text"
                value={userData?.p_cell_phone || ""}
                placeholder="___-___-____"
                className={
                  errorField?.includes("p_cell_phone")
                    ? `common-input-error`
                    : `common-input`
                }
                onChange={(e) => {
                  let str = e.target.value;
                  let filStr = str.replace(/[- ]/g, "");
                  if (filStr === "" || filStr.length === 0) {
                    setUserData({ ...userData, p_cell_phone: "" });
                    return;
                  }
                  if (!isNumber(filStr)) {
                    return;
                  }
                  if (filStr.length > 10) {
                    return;
                  }
                  if (errorField?.includes("p_cell_phone")) {
                    let errors = errorField.filter((y) => y !== "p_cell_phone");
                    setErrorField([...errors]);
                  }
                  if (filStr.length > 3 && filStr.length < 7) {
                    str = filStr.slice(0, 3) + "-" + filStr.slice(3);
                  }
                  if (filStr.length > 6) {
                    str = filStr.slice(0, 3) + "-" + filStr.slice(3, 6) + "-" + filStr.slice(6);
                  }
                  setUserData({ ...userData, p_cell_phone: str });
                }}
                style={{ textAlign: 'left' }}
              />
            </Col>
          </Row>
        </div>}
        {!isZeroAmount && <Divider />}
        {/* Payment Section - Show different content for facility vs individual */}
        {isZeroAmount ? (
          /* Zero Amount - No payment needed, just register */
          <div className="zero-amount-section">
            <div style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 4,
              padding: 16,
              marginBottom: 16
            }}>
              <p style={{ margin: 0, color: '#389e0d' }}>
                No payment is required for this registration.
              </p>
            </div>
            <CommonButton
              onClick={onPay}
              disabled={isBusy}
              customClass="pay-btn"
              style={{
                marginBottom: 8,
                width: '100%',
                height: 48,
                fontSize: 16
              }}
            >
              {isBusy ? 'Processing...' : 'Register'}
            </CommonButton>
          </div>
        ) : isFacilityType ? (
          /* Facility Application Section - No Payment Required */
          <div className="facility-application-section">
            <h3 style={{ textAlign: 'left' }}>Submit Application</h3>
            <div style={{
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: 4,
              padding: 16,
              marginBottom: 16
            }}>
              <p style={{ margin: 0, color: '#0050b3' }}>
                This is a facility reservation request. No payment is required at this time.
                Your application will be reviewed and you will be contacted regarding approval and any applicable fees.
              </p>
            </div>
            <CommonButton
              onClick={onSubmitFacilityApplication}
              disabled={isBusy}
              customClass="submit-application-btn"
              style={{
                marginBottom: 8,
                backgroundColor: '#52c41a',
                borderColor: '#52c41a',
                width: '100%',
                height: 48,
                fontSize: 16
              }}
            >
              {isBusy ? 'Submitting...' : 'Submit Application'}
            </CommonButton>
          </div>
        ) : (
          /* Individual Payment Section */
          <div className="payment-section">
            <h3 style={{ textAlign: 'left' }}>Payment Method</h3>
            <Radio.Group onChange={onChangeCardType} value={cardType} style={{ marginBottom: 16, textAlign: 'left' }}>
              <Radio.Button value="credit">Credit Card</Radio.Button>
              <Radio.Button value="debit">Debit Card</Radio.Button>
              <Radio.Button value="echeck">Bank (eCheck)</Radio.Button>
            </Radio.Group>
            {cardType === "echeck" ? (
              <BankPayment
                userData={userData}
                setUserData={setUserData}
                errorField={errorField || []}
                setErrorField={setErrorField}
                cardType={cardType}
                setCardType={setCardType}
                merchant={merchant}
                {...(merchantLower === "forte" && {
                  routingNumber,
                  setRoutingNumber,
                  accountNumber,
                  setAccountNumber,
                  accountType,
                  setAccountType,
                })}
              />
            ) : (
              <CardPayment
                userData={userData}
                setUserData={setUserData}
                errorField={errorField || []}
                setErrorField={setErrorField}
                cardType={cardType}
                setCardType={setCardType}
                merchant={merchant}
                {...(merchantLower === "forte" && {
                  cardNumber,
                  setCardNumber,
                  expiry,
                  setExpire,
                  cvc,
                  setCvc,
                })}
              />
            )}
            <Divider />
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <b style={{ textAlign: 'left' }}>Total Due: </b>
              <span style={{ fontSize: 18, color: '#1890ff', textAlign: 'right' }}>
                ${userData?.total_checkout || '0.00'}
              </span>
            </div>
            <CommonButton
              onClick={onPay}
              disabled={isPayButtonDisabled || isBusy || processorLoading}
              customClass="pay-btn"
              style={{ marginBottom: 8 }}
            >
              {isBusy ? 'Processing...' : 'Pay Now'}
            </CommonButton>
            {/* Show drop-off registration if allowed */}
            {allowManualPayments && (
              <NormalButton
                onClick={onDropOffPayment}
                disabled={isBusy}
                customClass="manual-pay-btn"
                style={{ marginBottom: 8 }}
              >
                Drop-Off Registration
              </NormalButton>
            )}
          </div>
        )}
        <PaymentProcessingModal
          visible={paymentModal.visible}
          status={paymentModal.status}
          message={paymentModal.message}
          error={paymentModal.error}
          onClose={() => setPaymentModal({ ...paymentModal, visible: false })}
        />
        <div style={{ textAlign: 'center', marginTop: 16, color: '#888', fontSize: 12 }}>
          Build: {buildInfo.build}
        </div>
      </div>
    </DebugErrorBoundary>
  );
};

export default StepCheckout;
